import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type { SerializedError } from '#features/ErrorHandling/errorHelpers'
import type { EnhancedLanguage, EnhancedMarket } from '#types/EnhancedMarket'

export interface MarketsState {
  markets: EnhancedMarket[]
  selectedCountryCode?: EnhancedMarket['countryCode']
  selectedLanguageCode?: EnhancedLanguage['languageCode']
  activeMarket?: EnhancedMarket
  activeLanguage?: EnhancedLanguage
  status: 'initial' | 'loading' | 'loaded' | 'failed'
  error?: SerializedError
}

const initialState: MarketsState = {
  markets: [],
  selectedCountryCode: undefined,
  selectedLanguageCode: undefined,
  activeMarket: undefined,
  activeLanguage: undefined,
  status: 'initial',
  error: undefined,
}

export const marketsSlice = createSlice({
  name: 'markets',
  initialState,
  reducers: {
    selectCountryCode: (state, action) => {
      state.selectedCountryCode = action.payload
      state.activeMarket = state.markets.find((market) => market.countryCode === action.payload)

      if (!state.activeMarket) {
        state.selectedLanguageCode = undefined
        state.activeLanguage = undefined
        return
      }

      const selectedLanguage = state.activeMarket?.languages?.find((language) => language.languageCode === state.selectedLanguageCode)
      state.activeLanguage = selectedLanguage ?? state.activeMarket?.languages?.[0]
      state.selectedLanguageCode = state.activeLanguage?.languageCode
    },
    selectLanguageCode: (state, action) => {
      if (!state.activeMarket) {
        state.selectedLanguageCode = action.payload
        state.activeLanguage = undefined
        return
      }

      const foundLanguage = state.activeMarket?.languages?.find((language) => language.languageCode === action.payload)
      state.activeLanguage = foundLanguage ?? state.activeMarket?.languages?.[0]
      state.selectedLanguageCode = state.activeLanguage?.languageCode
    },
    _setMarketStatus: (state, action: PayloadAction<Pick<MarketsState, 'status' | 'error'>>) => {
      state.status = action.payload.status
      state.error = action.payload.error
    },
    _setMarketData: (state, action: PayloadAction<EnhancedMarket[]>) => {
      state.markets = action.payload

      const selectedMarket = state.markets.find((market) => market.countryCode === state.selectedCountryCode)
      state.selectedCountryCode = selectedMarket?.countryCode ?? state.markets[0].countryCode
      state.activeMarket = selectedMarket ?? state.markets[0]

      const selectedLanguage = state.activeMarket?.languages?.find((language) => language.languageCode === state.selectedLanguageCode)
      state.activeLanguage = selectedLanguage ?? state.activeMarket?.languages?.[0]
      state.selectedLanguageCode = state.activeLanguage?.languageCode
    },
    _pushOrReplaceMarket: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        market: EnhancedMarket | null
      }>,
    ) => {
      // TODO does not handle change of marketId

      const marketId = action.payload.marketId
      const index = state.markets.findIndex((market) => market.marketId === marketId)

      if (action.payload.market === null) {
        if (index !== -1) {
          state.markets.splice(index, 1)
        }
        return
      }

      if (index !== -1) {
        state.markets[index] = action.payload.market
      } else {
        state.markets.push(action.payload.market)
      }
    },
    loadMarkets: (_state, _action: PayloadAction<{ forceReload?: boolean } | undefined>) => {
      // executed in the middleware
    },
  },
})

export const { selectCountryCode, selectLanguageCode, loadMarkets, _setMarketData, _setMarketStatus, _pushOrReplaceMarket } = marketsSlice.actions
export const marketsActions = marketsSlice.actions
